import {
  useGetWarehouses,
  getGetWarehousesQueryOptions,
  exportWarehouses,
} from "@/requests/gen/react-query/warehouse";
import { useGetProductBrands } from "@/requests/gen/react-query/product-brand";
import { useGetEntitiesInfinite } from "@/requests/gen/react-query/entity";
import {
  ProductTypeEnum,
  WarehouseSourceEnum,
  CountryEnum,
  EntityTypeEnum,
  ServiceEnum,
} from "@/requests/gen/react-query/fastAPI.schemas";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ParamDataTableToolbar,
  ParamSearchFilter,
  ParamClearButton,
  ParamDataTable,
  ParamRangeFilter,
  dataTableSearchParsers,
} from "@/components/ui/param-data-table";
import { Button } from "@/components/ui/button";
import { ColumnDef } from "@tanstack/react-table";
import {
  createFileRoute,
  Link,
  linkOptions,
  type LinkProps,
} from "@tanstack/react-router";
import {
  createLoader,
  parseAsArrayOf,
  parseAsStringEnum,
  parseAsInteger,
  parseAsBoolean,
  useQueryStates,
  type inferParserType,
} from "nuqs";
import { DropdownFilter } from "@/components/ui/dropdown-filter";
import { Archive, CircleHelp, FileBox, FlaskConical, Plus } from "lucide-react";
import { keepPreviousData } from "@tanstack/react-query";
import { Header } from "@/components/universal/header";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Toggle } from "@/components/ui/toggle";
import { countryOptions } from "../-country-options";
import { ExportCSV } from "@/components/ui/csv-export";
import { useOnlyEntity } from "@/hooks/use-only-entity";
import { useState } from "react";

const warehouseSearchParsers = {
  sources: parseAsArrayOf(
    parseAsStringEnum(Object.values(WarehouseSourceEnum)),
  ),
  product_types: parseAsArrayOf(
    parseAsStringEnum(Object.values(ProductTypeEnum)),
  ),
  reorder_level_min: parseAsInteger,
  reorder_level_max: parseAsInteger,
  product_brand_ids: parseAsArrayOf(parseAsInteger),
  countries: parseAsArrayOf(parseAsStringEnum(Object.values(CountryEnum))),
  is_sample: parseAsBoolean,
};

const warehouseSearchAllParsers = {
  ...dataTableSearchParsers,
  ...warehouseSearchParsers,
};

const loadWarehouseSearch = createLoader(warehouseSearchAllParsers);

type WarehouseSearch = inferParserType<typeof warehouseSearchAllParsers>;

function warehouseQueryParams(
  p: WarehouseSearch,
): Parameters<typeof getGetWarehousesQueryOptions>[0] {
  return {
    page: p.page,
    limit: p.limit,
    search: p.search,
    sort_column: p.sort_column,
    ascending: p.ascending,
    is_deleted: p.is_deleted,
    entity_ids: p.entity_ids,
    sources: p.sources,
    product_types: p.product_types,
    reorder_level_min: p.reorder_level_min,
    reorder_level_max: p.reorder_level_max,
    product_brand_ids: p.product_brand_ids,
    countries: p.countries,
    is_sample: p.is_sample ?? undefined,
  };
}

export const Route = createFileRoute(
  "/_private/_warehouse-360/warehouse/lookup",
)({
  loader: ({ context: { queryClient }, location }) =>
    queryClient.prefetchQuery(
      getGetWarehousesQueryOptions(
        warehouseQueryParams(loadWarehouseSearch(location.searchStr)),
      ),
    ),
  component: WarehouseLookup,
});

// Define the columns for the table
interface WarehouseTableProps {
  link?: LinkProps;
  name: string;
  is_sample: boolean;
  source: string;
  product_type: ProductTypeEnum;
  quantity_on_hand: number;
  incoming_quantity?: number;
  reorder_level: number | null;
  product_brand: string | null;
  stock_percentage: number | null;
}

// Define the columns headers and accessor keys
const columns: ColumnDef<WarehouseTableProps>[] = [
  {
    header: "Name",
    accessorKey: "name",
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <span>{row.original.name}</span>
        {row.original.is_sample && <Badge variant="secondary">Sample</Badge>}
      </div>
    ),
  },
  { header: "Source", accessorKey: "source" },
  { header: "Type", accessorKey: "product_type" },
  { header: "Quantity", accessorKey: "quantity_on_hand" },
  { header: "Incoming Quantity", accessorKey: "incoming_quantity" },
  { header: "Reorder Level", accessorKey: "reorder_level" },
  { header: "Brand", accessorKey: "product_brand" },
  {
    header: "% to Reorder",
    accessorKey: "stock_percentage",
    enableSorting: true,
    cell: ({ row }) => {
      // If there is no Reorder level, show a badge indicating this
      if (row.original.stock_percentage === null)
        return <Badge variant="destructive">--</Badge>;

      // If there is a Reorder level, show the stock percentage
      const badgeColor =
        row.original.stock_percentage >= 100
          ? "success"
          : row.original.stock_percentage > 33
            ? "warning"
            : "destructive";
      return (
        <Badge
          variant={badgeColor}
        >{`${row.original.stock_percentage}%`}</Badge>
      );
    },
  },
];

// Main component
function WarehouseLookup() {
  const [queryStates, setQueryStates] = useQueryStates(
    warehouseSearchAllParsers,
  );

  const [entitySearch, setEntitySearch] = useState<string | undefined>();
  const onlyEntity = useOnlyEntity();

  const entityIds = onlyEntity ? [onlyEntity] : queryStates.entity_ids;

  const warehouses = useGetWarehouses(
    warehouseQueryParams({ ...queryStates, entity_ids: entityIds }),
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );

  const productBrands = useGetProductBrands();

  const entities = useGetEntitiesInfinite(
    {
      search: entitySearch,
      include_ids: entityIds,
      entity_types: [EntityTypeEnum.Fulfiller],
      services: [ServiceEnum.warehouse_360],
    },
    {
      query: {
        placeholderData: keepPreviousData,
        getNextPageParam: (lastPage, allPages) =>
          lastPage.items.length === 0 ? undefined : allPages.length + 1,
      },
    },
  );

  const entityOptions =
    entities.data?.pages
      .flatMap((page) => page.items)
      .map((entity) => ({
        label: entity.name,
        value: entity.entity_id,
      })) || [];

  // Function to apply or clear the "Pre-Builds" preset
  const togglePreBuildsPreset = () => {
    if (isPreBuildsActive) {
      // Clear filters that define the preset
      setQueryStates({
        sources: null,
        product_types: null,
        reorder_level_min: null,
        reorder_level_max: null,
      });
    } else {
      // Apply filters that define the preset
      setQueryStates({
        sources: [WarehouseSourceEnum.Manufactured],
        product_types: [ProductTypeEnum.Kit],
        reorder_level_min: 1,
        reorder_level_max: null,
      });
    }
  };

  // Check if the current filters match the "Pre-builds" preset
  const isPreBuildsActive =
    queryStates.sources?.length === 1 &&
    queryStates.sources[0] === WarehouseSourceEnum.Manufactured &&
    queryStates.product_types?.length === 1 &&
    queryStates.product_types[0] === ProductTypeEnum.Kit &&
    queryStates.reorder_level_min === 1 &&
    !queryStates.reorder_level_max;

  // Map the data to the table format
  const tableData: WarehouseTableProps[] | undefined =
    warehouses.data?.items.map((warehouse) => {
      return {
        link: linkOptions({
          to: "/warehouse/$warehouseId",
          params: { warehouseId: String(warehouse.warehouse_id) },
        }),
        name: warehouse.catalog_product.display_name,
        is_sample: warehouse.catalog_product.is_sample ?? false,
        source: warehouse.source,
        product_type: warehouse.catalog_product.product_base.product_type,
        quantity_on_hand: warehouse.quantity_on_hand ?? 0,
        incoming_quantity: warehouse.incoming_quantity ?? 0,
        reorder_level: warehouse.reorder_level ?? null,
        product_brand: warehouse.catalog_product.product_brand.name ?? null,
        stock_percentage: warehouse.stock_percentage ?? null,
      };
    });

  return (
    <div>
      <title>Warehouse - Legrande Health Portal</title>
      <Header
        crumbs={[{ name: "Warehouse", link: { to: "/warehouse/lookup" } }]}
      />
      <Card>
        <CardHeader>
          <CardTitle>Warehouse</CardTitle>
          <CardDescription>
            View your warehouse items below. You can search, filter, and click
            to see more details.
          </CardDescription>
          <CardAction>
            <Button variant="default" size="sm" asChild>
              <Link to="/warehouse/create">
                <Plus />
                Add Item
              </Link>
            </Button>
          </CardAction>
        </CardHeader>
        <CardContent>
          <ParamDataTableToolbar>
            <ParamSearchFilter isFetching={warehouses.isFetching} />
            {!onlyEntity && (
              <DropdownFilter
                title="Entity"
                search={entitySearch}
                fetchNextPage={entities.fetchNextPage}
                isFetching={entities.isFetchingNextPage}
                setSearch={setEntitySearch}
                options={[{ items: entityOptions }]}
                values={queryStates.entity_ids}
                onChange={(values) => setQueryStates({ entity_ids: values })}
              />
            )}
            <DropdownFilter
              title="Source"
              options={[
                {
                  items: Object.values(WarehouseSourceEnum).map((source) => {
                    return { label: source, value: source };
                  }),
                },
              ]}
              values={queryStates.sources || []}
              onChange={(values) => setQueryStates({ sources: values })}
            />
            <DropdownFilter
              title="Type"
              options={[
                {
                  items: Object.values(ProductTypeEnum).map((product_type) => {
                    return { label: product_type, value: product_type };
                  }),
                },
              ]}
              values={queryStates.product_types || []}
              onChange={(values) => setQueryStates({ product_types: values })}
            />
            <ParamRangeFilter
              title="Reorder Level"
              minValue={queryStates.reorder_level_min}
              maxValue={queryStates.reorder_level_max}
              onChange={(min, max) =>
                setQueryStates({
                  reorder_level_min: min,
                  reorder_level_max: max,
                })
              }
              step={50}
            />
            <DropdownFilter
              title="Brand"
              options={[
                {
                  items:
                    productBrands.data?.map((brand) => ({
                      label: brand.name,
                      value: brand.product_brand_id,
                    })) ?? [],
                },
              ]}
              values={queryStates.product_brand_ids || []}
              onChange={(values) =>
                setQueryStates({ product_brand_ids: values })
              }
            />
            <DropdownFilter
              title="Country"
              options={countryOptions}
              values={queryStates.countries || []}
              onChange={(values) => setQueryStates({ countries: values })}
            />

            {/* Preset */}
            <Toggle
              pressed={isPreBuildsActive}
              onPressedChange={togglePreBuildsPreset}
              size="default"
              className="flex items-center gap-1"
            >
              <FileBox />
              Stock 360
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="ml-1">
                    <CircleHelp className="text-muted-foreground size-4 cursor-pointer" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  Kits manufactured in-house with a reorder level of at least 1.
                </TooltipContent>
              </Tooltip>
            </Toggle>
            <Toggle
              pressed={queryStates.is_deleted || false}
              onPressedChange={(pressed) =>
                setQueryStates({ is_deleted: pressed })
              }
            >
              <Archive />
              Archived
            </Toggle>
            <Toggle
              pressed={queryStates.is_sample ?? false}
              onPressedChange={(pressed) =>
                setQueryStates({ is_sample: pressed ? true : null })
              }
            >
              <FlaskConical />
              Samples
            </Toggle>
            <ParamClearButton />
          </ParamDataTableToolbar>
          <ParamDataTable
            columns={columns}
            data={tableData ?? []}
            isLoading={warehouses.isLoading}
            isError={warehouses.isError}
            count={warehouses.data?.count}
            footerActions={
              <ExportCSV
                onExport={() =>
                  exportWarehouses({
                    search: queryStates.search,
                    sort_column: queryStates.sort_column,
                    ascending: queryStates.ascending,
                    is_deleted: queryStates.is_deleted,
                    entity_ids: entityIds,
                    sources: queryStates.sources,
                    product_types: queryStates.product_types,
                    reorder_level_min: queryStates.reorder_level_min,
                    reorder_level_max: queryStates.reorder_level_max,
                    product_brand_ids: queryStates.product_brand_ids,
                    countries: queryStates.countries,
                    is_sample: queryStates.is_sample ?? undefined,
                    hidden_columns: queryStates.hidden,
                  })
                }
                filename="warehouse"
                requiresPHIWarning={false}
              />
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}
